/**
 * MUI Component Overrides — Modern Enterprise Design System
 *
 * Clean, minimal overrides for a professional, modern appearance.
 * Inspired by Linear, Vercel, and Raycast component styling.
 */

import { alpha } from '@mui/material/styles';
import {
  COLOR_PRIMARY,
  COLOR_BORDER,
  RADIUS_MD,
  RADIUS_LG,
  RADIUS_SM,
  DURATION_FAST,
  DURATION_NORMAL,
  EASE_DEFAULT,
} from './tokens';

const componentOverrides = {
  // ── CssBaseline ─────────────────────────────────────────────────────────────
  MuiCssBaseline: {
    styleOverrides: {
      '*, *::before, *::after': {
        boxSizing: 'border-box',
      },
      html: {
        scrollBehavior: 'smooth',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
      },
      '*:focus-visible': {
        outline: `2px solid ${COLOR_PRIMARY}`,
        outlineOffset: '2px',
      },
      body: {
        transition: `background-color ${DURATION_NORMAL} ${EASE_DEFAULT}, color ${DURATION_NORMAL} ${EASE_DEFAULT}`,
      },
      // Modern scrollbar
      '*::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
      },
      '*::-webkit-scrollbar-track': {
        background: 'transparent',
      },
      '*::-webkit-scrollbar-thumb': {
        background: 'rgba(0,0,0,0.12)',
        borderRadius: '3px',
      },
      '*::-webkit-scrollbar-thumb:hover': {
        background: 'rgba(0,0,0,0.2)',
      },
    },
  },

  // ── Button ──────────────────────────────────────────────────────────────────
  MuiButton: {
    defaultProps: {
      disableElevation: true,
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        borderRadius: RADIUS_MD,
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.8125rem',
        transition: `all ${DURATION_FAST} ${EASE_DEFAULT}`,
        '&:active': {
          transform: 'scale(0.98)',
        },
      },
      sizeSmall: {
        height: 30,
        paddingLeft: 10,
        paddingRight: 10,
        fontSize: '0.75rem',
      },
      sizeMedium: {
        height: 34,
        paddingLeft: 14,
        paddingRight: 14,
      },
      sizeLarge: {
        height: 40,
        paddingLeft: 20,
        paddingRight: 20,
        fontSize: '0.875rem',
      },
      contained: {
        boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
        '&:hover': {
          boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)',
        },
      },
      outlined: {
        borderColor: COLOR_BORDER,
        '&:hover': {
          borderColor: COLOR_PRIMARY,
          backgroundColor: 'rgba(37, 99, 235, 0.04)',
        },
      },
    },
  },

  // ── Icon Button ─────────────────────────────────────────────────────────────
  MuiIconButton: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        borderRadius: RADIUS_MD,
        transition: `all ${DURATION_FAST} ${EASE_DEFAULT}`,
        '&:hover': {
          backgroundColor: 'rgba(15, 23, 42, 0.05)',
        },
      },
    },
  },

  // ── Card ────────────────────────────────────────────────────────────────────
  MuiCard: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: RADIUS_LG,
        border: `1px solid ${COLOR_BORDER}`,
        transition: `all ${DURATION_NORMAL} ${EASE_DEFAULT}`,
        overflow: 'hidden',
      },
    },
  },

  // ── Paper ───────────────────────────────────────────────────────────────────
  MuiPaper: {
    defaultProps: {
      elevation: 0,
    },
    styleOverrides: {
      root: {
        borderRadius: RADIUS_LG,
        backgroundImage: 'none',
      },
    },
  },

  // ── TextField ───────────────────────────────────────────────────────────────
  MuiTextField: {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: RADIUS_MD,
          fontSize: '0.8125rem',
          transition: `border-color ${DURATION_FAST} ${EASE_DEFAULT}, box-shadow ${DURATION_FAST} ${EASE_DEFAULT}`,
        },
      },
    },
  },

  // ── OutlinedInput ───────────────────────────────────────────────────────────
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS_MD,
        fontSize: '0.8125rem',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: COLOR_BORDER,
          transition: `border-color ${DURATION_FAST} ${EASE_DEFAULT}`,
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#94a3b8',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: COLOR_PRIMARY,
          borderWidth: '1.5px',
          boxShadow: `0 0 0 3px rgba(37, 99, 235, 0.08)`,
        },
      },
      sizeSmall: {
        minHeight: 36,
      },
    },
  },

  // ── InputLabel ──────────────────────────────────────────────────────────────
  MuiInputLabel: {
    styleOverrides: {
      root: {
        fontSize: '0.8125rem',
        transform: 'translate(14px, 9px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -9px) scale(0.75)',
          backgroundColor: 'var(--mui-palette-background-paper, #fff)',
          padding: '0 4px',
        },
      },
      sizeSmall: {
        fontSize: '0.8125rem',
        transform: 'translate(14px, 8px) scale(1)',
        '&.MuiInputLabel-shrink': {
          transform: 'translate(14px, -9px) scale(0.75)',
        },
      },
    },
  },

  // ── Select ──────────────────────────────────────────────────────────────────
  MuiSelect: {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      select: {
        fontSize: '0.8125rem',
      },
    },
  },

  // ── Chip ────────────────────────────────────────────────────────────────────
  MuiChip: {
    defaultProps: {
      size: 'small',
    },
    styleOverrides: {
      root: {
        borderRadius: '6px',
        fontWeight: 500,
        fontSize: '0.75rem',
        height: 24,
        transition: `all ${DURATION_FAST} ${EASE_DEFAULT}`,
        maxWidth: '100%',
        '& .MuiChip-label': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        // Give status/label badges a consistent minimum footprint so short-label
        // chips (e.g. "high", "6d", "Mine", "ACTIVE") render at a uniform size
        // across the app. Long labels still grow and ellipsise. Deletable chips
        // (removable tags) and chips with an avatar/icon are excluded so tag
        // inputs and "+N" style counters are unaffected.
        '&:not(.MuiChip-deletable)': {
          minWidth: 80,
          '& .MuiChip-label': {
            width: '100%',
            textAlign: 'center',
          },
        },
      },
      sizeSmall: {
        height: 22,
        fontSize: '0.6875rem',
      },
    },
  },

  // ── Tooltip ─────────────────────────────────────────────────────────────────
  MuiTooltip: {
    defaultProps: {
      enterDelay: 300,
      leaveDelay: 50,
    },
    styleOverrides: {
      tooltip: {
        backgroundColor: '#0f172a',
        color: '#f8fafc',
        borderRadius: RADIUS_SM,
        fontSize: '0.75rem',
        fontWeight: 450,
        padding: '5px 10px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        border: '1px solid rgba(255,255,255,0.06)',
      },
      arrow: {
        color: '#0f172a',
      },
    },
  },

  // ── LinearProgress ──────────────────────────────────────────────────────────
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        height: 3,
        borderRadius: 2,
        backgroundColor: 'rgba(37, 99, 235, 0.08)',
      },
    },
  },

  // ── Skeleton ────────────────────────────────────────────────────────────────
  MuiSkeleton: {
    defaultProps: {
      animation: 'wave',
    },
    styleOverrides: {
      rectangular: {
        borderRadius: RADIUS_MD,
      },
      rounded: {
        borderRadius: RADIUS_LG,
      },
    },
  },

  // ── Dialog ──────────────────────────────────────────────────────────────────
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: RADIUS_LG,
        boxShadow: '0 24px 48px -12px rgba(0,0,0,0.18)',
      },
      backdrop: {
        backgroundColor: 'rgba(15, 23, 42, 0.4)',
        backdropFilter: 'blur(4px)',
      },
    },
  },

  // ── Menu (dropdown) ─────────────────────────────────────────────────────────
  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: RADIUS_MD,
        boxShadow: '0 8px 24px -4px rgba(0,0,0,0.12), 0 4px 8px -4px rgba(0,0,0,0.06)',
        marginTop: '4px',
        minWidth: 180,
      },
      list: {
        padding: '4px',
      },
    },
  },

  // ── MenuItem ────────────────────────────────────────────────────────────────
  MuiMenuItem: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        borderRadius: RADIUS_SM,
        margin: '1px 0',
        padding: '7px 10px',
        fontSize: '0.8125rem',
        transition: `background-color ${DURATION_FAST} ${EASE_DEFAULT}`,
        '&:hover': {
          backgroundColor: 'rgba(15, 23, 42, 0.04)',
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(37, 99, 235, 0.06)',
          '&:hover': {
            backgroundColor: 'rgba(37, 99, 235, 0.1)',
          },
        },
      },
    },
  },

  // ── Tabs ────────────────────────────────────────────────────────────────────
  MuiTab: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.8125rem',
        minHeight: 36,
        padding: '6px 12px',
        transition: `color ${DURATION_FAST} ${EASE_DEFAULT}`,
        '&.Mui-selected': {
          fontWeight: 600,
        },
      },
    },
  },

  MuiTabs: {
    styleOverrides: {
      indicator: {
        height: 2,
        borderRadius: '2px 2px 0 0',
      },
    },
  },

  // ── Alert ───────────────────────────────────────────────────────────────────
  MuiAlert: {
    styleOverrides: {
      root: {
        borderRadius: RADIUS_MD,
        fontSize: '0.8125rem',
        border: '1px solid',
        alignItems: 'center',
      },
    },
  },

  // ── Switch ──────────────────────────────────────────────────────────────────
  MuiSwitch: {
    styleOverrides: {
      root: {
        padding: 8,
      },
      track: {
        borderRadius: 11,
        opacity: 1,
        backgroundColor: '#e2e8f0',
      },
      thumb: {
        boxShadow: '0 1px 3px 0 rgba(0,0,0,0.12)',
      },
    },
  },

  // ── Breadcrumbs ─────────────────────────────────────────────────────────────
  MuiBreadcrumbs: {
    styleOverrides: {
      root: {
        fontSize: '0.75rem',
      },
      separator: {
        marginLeft: 4,
        marginRight: 4,
        color: '#94a3b8',
      },
    },
  },

  // ── Divider ─────────────────────────────────────────────────────────────────
  MuiDivider: {
    styleOverrides: {
      root: {
        borderColor: COLOR_BORDER,
      },
    },
  },

  // ── Avatar ──────────────────────────────────────────────────────────────────
  MuiAvatar: {
    styleOverrides: {
      root: {
        fontSize: '0.8125rem',
        fontWeight: 600,
      },
    },
  },

  // ── Table (theme-aware: header tint, colored separators, zebra striping) ─────
  MuiTableHead: {
    styleOverrides: {
      root: ({ theme }) => {
        const isDark = theme.palette.mode === 'dark';
        const primary = theme.palette.primary.main;
        return {
          '& .MuiTableCell-root': {
            fontSize: '0.6875rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: isDark ? theme.palette.primary.light : theme.palette.primary.dark,
            backgroundColor: alpha(primary, isDark ? 0.12 : 0.07),
            borderBottom: `2px solid ${alpha(primary, isDark ? 0.35 : 0.22)}`,
            padding: '10px 16px',
            whiteSpace: 'nowrap',
            // Colored vertical separators between header columns
            '&:not(:last-of-type)': {
              borderRight: `1px solid ${alpha(primary, isDark ? 0.18 : 0.14)}`,
            },
          },
        };
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: ({ theme }) => {
        const isDark = theme.palette.mode === 'dark';
        const primary = theme.palette.primary.main;
        return {
          fontSize: '0.8125rem',
          borderBottom: `1px solid ${theme.palette.divider}`,
          padding: '12px 16px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          // Colored vertical separators between body columns
          '&:not(:last-of-type)': {
            borderRight: `1px solid ${alpha(primary, isDark ? 0.06 : 0.05)}`,
          },
        };
      },
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: ({ theme }) => {
        const isDark = theme.palette.mode === 'dark';
        const primary = theme.palette.primary.main;
        return {
          transition: `background-color ${DURATION_FAST} ${EASE_DEFAULT}`,
          // Zebra striping for body rows (head rows are excluded via :not)
          '&:nth-of-type(even):not(.MuiTableRow-head)': {
            backgroundColor: alpha(primary, isDark ? 0.03 : 0.025),
          },
          '&:hover:not(.MuiTableRow-head)': {
            backgroundColor: alpha(primary, isDark ? 0.12 : 0.07),
          },
          // Header cells inside a head row must not pick up the zebra/hover tint
          '&.MuiTableRow-head': {
            backgroundColor: 'transparent',
          },
        };
      },
    },
  },

  // ── DataGrid (raw MUI X grids that bypass the shared DataTable) ──────────────
  MuiDataGrid: {
    styleOverrides: {
      root: ({ theme }) => {
        const isDark = theme.palette.mode === 'dark';
        const primary = theme.palette.primary.main;
        return {
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: RADIUS_LG,
          fontSize: '0.8125rem',
          '& .MuiDataGrid-columnHeaders': {
            position: 'relative',
            backgroundColor: alpha(primary, isDark ? 0.12 : 0.07),
            borderBottom: `2px solid ${alpha(primary, isDark ? 0.35 : 0.22)}`,
          },
          '& .MuiDataGrid-columnHeaders::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, ${theme.palette.info.main} 100%)`,
            zIndex: 3,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: isDark ? theme.palette.primary.light : theme.palette.primary.dark,
          },
          '& .MuiDataGrid-columnHeader:not(:last-of-type)': {
            borderRight: `1px solid ${alpha(primary, isDark ? 0.18 : 0.14)}`,
          },
          '& .MuiDataGrid-columnSeparator': { visibility: 'hidden' },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
          '& .MuiDataGrid-cell:not(:last-of-type)': {
            borderRight: `1px solid ${alpha(primary, isDark ? 0.06 : 0.05)}`,
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: alpha(primary, isDark ? 0.03 : 0.025),
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: alpha(primary, isDark ? 0.12 : 0.07),
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: alpha(primary, isDark ? 0.04 : 0.03),
          },
        };
      },
    },
  },

  // ── Accordion ───────────────────────────────────────────────────────────────
  MuiAccordion: {
    defaultProps: {
      disableGutters: true,
      elevation: 0,
    },
    styleOverrides: {
      root: {
        border: `1px solid ${COLOR_BORDER}`,
        borderRadius: `${RADIUS_MD} !important`,
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          margin: 0,
        },
      },
    },
  },

  // ── Badge ───────────────────────────────────────────────────────────────────
  MuiBadge: {
    styleOverrides: {
      badge: {
        fontSize: '0.625rem',
        fontWeight: 700,
        minWidth: 18,
        height: 18,
        padding: '0 5px',
        lineHeight: 1,
        borderRadius: '9px',
      },
    },
  },

  // ── List ────────────────────────────────────────────────────────────────────
  MuiListItemButton: {
    defaultProps: {
      disableRipple: true,
    },
    styleOverrides: {
      root: {
        borderRadius: RADIUS_SM,
        transition: `background-color ${DURATION_FAST} ${EASE_DEFAULT}`,
      },
    },
  },
};

export default componentOverrides;
